import { useState, useEffect } from 'react';
import { Box, Typography, useTheme, Chip, IconButton, Tooltip, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import { getUnits, formatPace, formatVerticalSpeed, convertDistance } from '../../utils/units';

import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import RamenDiningIcon from '@mui/icons-material/RamenDining';
import LuggageIcon from '@mui/icons-material/Luggage';
import HotelIcon from '@mui/icons-material/Hotel';
import GroupIcon from '@mui/icons-material/Group';
import TimerIcon from '@mui/icons-material/Timer';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';

// ---------------------------------------------------------------------------
// Colours — reuse the same palettes as the Climbs / Descents grids
// ---------------------------------------------------------------------------
const CLIMB_COLORS = {
  'R-C4': '#ffe066',
  'R-C3': '#ffa020',
  'R-C2': '#e05000',
  'R-C1': '#c01010',
  'R-HC': '#7a0000',
};

const DESCENT_COLORS = {
  'R-D4': '#38bdf8',
  'R-D3': '#3b82f6',
  'R-D2': '#6366f1',
  'R-D1': '#7c3aed',
  'R-DHC': '#9333ea',
};

const CHECKPOINT_COLOR = '#64b5f6';

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------
const formatDuration = (secs) => {
  if (secs == null || secs <= 0) return '-';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const formatElapsed = (secs) => {
  if (secs == null) return '-';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m`;
};

const getUtcOffsetMinutes = (isoString) => {
  if (!isoString) return null;
  const match = isoString.match(/([+-])(\d{2}):(\d{2})$/);
  if (!match) return null;
  const sign = match[1] === '+' ? 1 : -1;
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
};

const formatTiming = (elapsedSeconds, workoutStartTime) => {
  if (elapsedSeconds == null || !workoutStartTime) return '-';
  const startMs = new Date(workoutStartTime).getTime();
  const arrivalMs = startMs + elapsedSeconds * 1000;
  const offsetMinutes = getUtcOffsetMinutes(workoutStartTime);
  if (offsetMinutes !== null) {
    const offsetMs = offsetMinutes * 60000;
    const arrivalLocalMs = arrivalMs + offsetMs;
    const startLocalMs   = startMs   + offsetMs;
    const startLocalDate = new Date(startLocalMs);
    const startMidnightLocalMs = startLocalMs - (
      startLocalDate.getUTCHours()        * 3600000 +
      startLocalDate.getUTCMinutes()      * 60000   +
      startLocalDate.getUTCSeconds()      * 1000    +
      startLocalDate.getUTCMilliseconds()
    );
    const dayNum = Math.floor((arrivalLocalMs - startMidnightLocalMs) / 86400000) + 1;
    const d = new Date(arrivalLocalMs);
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `Day ${dayNum} ${hh}:${mm}`;
  }
  const startMidnight = new Date(workoutStartTime);
  startMidnight.setHours(0, 0, 0, 0);
  const dayNum = Math.floor((arrivalMs - startMidnight.getTime()) / 86400000) + 1;
  const d = new Date(arrivalMs);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `Day ${dayNum} ${hh}:${mm}`;
};

const parseCutoffMs = (cutoffStr, workoutStartTime) => {
  if (!cutoffStr || !workoutStartTime) return null;
  const match = cutoffStr.match(/Day\s*(\d+)\s+(\d{1,2}):(\d{2})/i);
  if (!match) return null;
  const dayNum  = parseInt(match[1], 10);
  const hours   = parseInt(match[2], 10);
  const minutes = parseInt(match[3], 10);
  const startMs = new Date(workoutStartTime).getTime();
  const offsetMinutes = getUtcOffsetMinutes(workoutStartTime);
  const offsetMs = (offsetMinutes !== null ? offsetMinutes : 0) * 60000;
  const startLocalMs = startMs + offsetMs;
  const startLocalDate = new Date(startLocalMs);
  const startMidnightLocalMs = startLocalMs - (
    startLocalDate.getUTCHours()        * 3600000 +
    startLocalDate.getUTCMinutes()      * 60000   +
    startLocalDate.getUTCSeconds()      * 1000    +
    startLocalDate.getUTCMilliseconds()
  );
  const cutoffLocalMs = startMidnightLocalMs + (dayNum - 1) * 86400000 + hours * 3600000 + minutes * 60000;
  return cutoffLocalMs - offsetMs;
};

// ---------------------------------------------------------------------------
// Type chip renderer
// ---------------------------------------------------------------------------
const TypeChip = ({ row, colors }) => {
  if (row.segment_type === 'checkpoint') {
    return (
      <Box display="flex" gap={0.5} alignItems="center">
        <PauseCircleIcon sx={{ fontSize: 16, color: CHECKPOINT_COLOR }} />
      </Box>
    );
  }
  if (row.segment_type === 'climb') {
    const cat = (row.segment_detail || '').split(' ')[0];
    return (
      <Chip
        label={row.segment_detail || 'Climb'}
        size="small"
        sx={{
          backgroundColor: CLIMB_COLORS[cat] || '#ffa020',
          color: cat === 'R-C4' ? '#3a2800' : '#fff',
          fontWeight: 'bold',
          fontSize: '0.65rem',
          height: 20,
        }}
      />
    );
  }
  if (row.segment_type === 'descend') {
    const cat = (row.segment_detail || '').split(' ')[0];
    return (
      <Chip
        label={row.segment_detail || 'Descent'}
        size="small"
        sx={{
          backgroundColor: DESCENT_COLORS[cat] || '#3b82f6',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '0.65rem',
          height: 20,
        }}
      />
    );
  }
  if (row.segment_type === 'mixed') {
    return (
      <Chip
        label={row.segment_detail || 'Mixed'}
        size="small"
        sx={{
          backgroundColor: '#1a4a2e',
          color: '#86efac',
          fontWeight: 'bold',
          fontSize: '0.65rem',
          height: 20,
        }}
      />
    );
  }
  return (
    <Chip
      label="Rolling"
      size="small"
      sx={{
        backgroundColor: colors.grey[700],
        color: colors.grey[300],
        fontSize: '0.65rem',
        height: 20,
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Service icons renderer (for checkpoint stop rows)
// ---------------------------------------------------------------------------
const ServiceIcons = ({ services, colors }) => {
  if (!services) return (
    <TimerIcon sx={{ fontSize: 16, color: colors.grey[500] }} title="Timing point" />
  );
  return (
    <Box display="flex" gap={0.5} alignItems="center">
      {services.food              && <LocalDiningIcon sx={{ fontSize: 16, color: '#4caf50' }} title="Food" />}
      {services.drinks            && <LocalCafeIcon   sx={{ fontSize: 16, color: '#42a5f5' }} title="Drinks" />}
      {services.warm_meal         && <RamenDiningIcon  sx={{ fontSize: 16, color: '#ff9800' }} title="Warm meal" />}
      {services.spare_bag         && <LuggageIcon      sx={{ fontSize: 16, color: '#90caf9' }} title="Spare bag" />}
      {services.rest_area         && <HotelIcon        sx={{ fontSize: 16, color: '#a5d6a7' }} title="Rest area" />}
      {services.assistance_permitted && <GroupIcon     sx={{ fontSize: 16, color: '#bdbdbd' }} title="Crew" />}
      {!services.food && !services.drinks && !services.warm_meal && !services.spare_bag &&
       !services.rest_area && !services.assistance_permitted && (
        <TimerIcon sx={{ fontSize: 16, color: colors.grey[500] }} title="Timing point" />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
// Tallest the grid is allowed to get before it scrolls internally, so a 56-segment
// ultra plan doesn't turn the page into an endless scroll.
const GRID_MAX_HEIGHT = 900;
// Rows that fit inside GRID_MAX_HEIGHT once the grid's own chrome is accounted for
// ((900 - 162) / 52). At or below this the grid sizes itself to its content.
const AUTO_HEIGHT_MAX_ROWS = 14;

const RacePlanGrid = ({ segments = [], race = null, workoutStartTime = null, onSegmentSelect = null, onSegmentDblClick = null,
  onInsertCheckpoint = null, onEditCheckpoint = null, onDeleteCheckpoint = null, onAddCheckpoint = null, checkpointBusy = false }) => {
  const theme  = useTheme();
  const colors = tokens(theme.palette.mode);
  const [units, setUnits] = useState(getUnits());
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const handler = (e) => setUnits(e.detail || getUnits());
    window.addEventListener('unitsChanged', handler);
    return () => window.removeEventListener('unitsChanged', handler);
  }, []);

  const hasRace    = !!race;
  const hasCutoffs = hasRace && segments.some(s => s.cutoff);
  // Timing (Day X HH:MM) only makes sense when a race is attached — the race defines the
  // reference start time. For plain workouts workout_start_time is the GPS recording time
  // which gives no useful wall-clock meaning for planning.
  const hasTiming  = hasRace && workoutStartTime != null && segments.some(s => s.elapsed_s != null);
  const hasAscVS   = segments.some(s => s.moving_asc_vertical_speed != null);
  const hasDescVS  = segments.some(s => s.moving_desc_vertical_speed != null);

  // Compute cumulative elevation gain — checkpoints carry the running total of their predecessors
  let _cumGain = 0;
  const rows = segments.map((seg, idx) => {
    if (seg.segment_type !== 'checkpoint') _cumGain += (seg.elevation_gain_m || 0);
    return { id: idx, ...seg, cum_elevation_gain_m: _cumGain };
  });

  const useAutoHeight = rows.length <= AUTO_HEIGHT_MAX_ROWS;

  const columns = [
    // Segment start → end label
    {
      field: 'start_label',
      headerName: 'Segment',
      flex: 1,
      minWidth: 160,
      valueFormatter: (_val, row) =>
        row.segment_type === 'checkpoint' ? `[Stop] ${row.start_label}` : `${row.start_label} → ${row.end_label}`,
      renderCell: (params) => {
        const row = params.row;
        const isStop = row.segment_type === 'checkpoint';
        return (
          <Box display="flex" flexDirection="column" justifyContent="center" py={0.5}>
            {isStop ? (
              <Typography variant="body2" color={CHECKPOINT_COLOR} fontWeight="bold">
                {row.start_label}
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color={colors.grey[400]} lineHeight={1.2}>
                  {row.start_label}
                </Typography>
                <Typography variant="body2" color={colors.grey[100]} fontWeight="bold" lineHeight={1.2}>
                  {row.end_label}
                </Typography>
              </>
            )}
          </Box>
        );
      },
    },
    // Type chip / services icons
    {
      field: 'segment_type',
      headerName: 'Type',
      width: 130,
      renderCell: (params) => {
        const row = params.row;
        if (row.segment_type === 'checkpoint') {
          return (
            <Box display="flex" alignItems="center" gap={0.5}>
              <ServiceIcons services={row.services} colors={colors} />
            </Box>
          );
        }
        return <TypeChip row={row} colors={colors} />;
      },
    },
    // Start
    {
      field: 'start_distance_m',
      headerName: units === 'imperial' ? 'Start mi' : 'Start km',
      width: 80,
      valueFormatter: (value, row) => convertDistance(row.segment_type === 'checkpoint' ? row.start_distance_m : value, units).toFixed(1),
      renderCell: (params) => {
        const row = params.row;
        const dist = row.segment_type === 'checkpoint' ? row.start_distance_m : params.value;
        return (
          <Typography variant="body2" color={colors.grey[400]}>
            {convertDistance(dist, units).toFixed(1)}
          </Typography>
        );
      },
    },
    // End
    {
      field: 'end_distance_m',
      headerName: units === 'imperial' ? 'End mi' : 'End km',
      width: 80,
      valueFormatter: (value, row) => convertDistance(row.segment_type === 'checkpoint' ? row.start_distance_m : value, units).toFixed(1),
      renderCell: (params) => {
        const row = params.row;
        const dist = row.segment_type === 'checkpoint' ? row.start_distance_m : params.value;
        return (
          <Typography variant="body2" color={colors.grey[100]}>
            {convertDistance(dist, units).toFixed(1)}
          </Typography>
        );
      },
    },
    // Length
    {
      field: 'length_m',
      headerName: 'Length',
      width: 80,
      valueFormatter: (value, row) => row.segment_type === 'checkpoint' ? '' : `${convertDistance(value, units).toFixed(1)} ${units === 'imperial' ? 'mi' : 'km'}`,
      renderCell: (params) => {
        const row = params.row;
        if (row.segment_type === 'checkpoint' || !params.value) return null;
        return (
          <Typography variant="body2" color={colors.grey[300]}>
            {convertDistance(params.value, units).toFixed(1)} {units === 'imperial' ? 'mi' : 'km'}
          </Typography>
        );
      },
    },
    // Duration
    {
      field: 'duration_s',
      headerName: 'Duration',
      width: 95,
      valueFormatter: (_val, row) => {
        const val = row.segment_type === 'checkpoint' ? row.stopping_time_s : row.duration_s;
        return formatDuration(val);
      },
      renderCell: (params) => {
        const row = params.row;
        const isStop = row.segment_type === 'checkpoint';
        const val    = isStop ? row.stopping_time_s : params.value;
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            {isStop && val > 0 && (
              <PauseCircleIcon sx={{ fontSize: 13, color: CHECKPOINT_COLOR }} />
            )}
            <Typography variant="body2" color={isStop ? CHECKPOINT_COLOR : colors.grey[100]}>
              {formatDuration(val)}
            </Typography>
          </Box>
        );
      },
    },
    // Gain
    {
      field: 'elevation_gain_m',
      headerName: 'Gain',
      width: 70,
      valueFormatter: (value, row) => (row.segment_type === 'checkpoint' || !value) ? '' : `+${Math.round(value)}m`,
      renderCell: (params) => {
        const row = params.row;
        if (row.segment_type === 'checkpoint' || !params.value) return null;
        return (
          <Typography variant="body2" color="#ef9a9a">
            +{Math.round(params.value)}m
          </Typography>
        );
      },
    },
    // Loss
    {
      field: 'elevation_loss_m',
      headerName: 'Loss',
      width: 70,
      valueFormatter: (value, row) => (row.segment_type === 'checkpoint' || !value) ? '' : `-${Math.round(value)}m`,
      renderCell: (params) => {
        const row = params.row;
        if (row.segment_type === 'checkpoint' || !params.value) return null;
        return (
          <Typography variant="body2" color="#80cbc4">
            -{Math.round(params.value)}m
          </Typography>
        );
      },
    },
    // Cumulative elevation gain
    {
      field: 'cum_elevation_gain_m',
      headerName: '∑ D+',
      width: 75,
      valueFormatter: (value) => value ? `+${Math.round(value)}m` : '-',
      renderCell: (params) => (
        <Typography variant="body2" color={params.value ? '#ef9a9a' : colors.grey[600]}>
          {params.value ? `+${Math.round(params.value)}m` : '-'}
        </Typography>
      ),
    },
    // Elapsed from workout start
    {
      field: 'elapsed_s',
      headerName: 'Elapsed',
      width: 90,
      valueFormatter: (value) => formatElapsed(value),
      renderCell: (params) => (
        <Typography variant="body2" color={params.value != null ? '#a5d6a7' : colors.grey[500]}>
          {formatElapsed(params.value)}
        </Typography>
      ),
    },
    // Absolute timing (Day X HH:MM) — only if workout_start_time available
    ...(hasTiming ? [{
      field: 'elapsed_s_timing',
      headerName: 'Timing',
      width: 115,
      valueGetter: (_val, row) => row.elapsed_s,
      valueFormatter: (value) => formatTiming(value, workoutStartTime),
      renderCell: (params) => {
        const formatted = formatTiming(params.value, workoutStartTime);
        return (
          <Typography variant="caption" color={formatted !== '-' ? colors.grey[100] : colors.grey[500]}>
            {formatted}
          </Typography>
        );
      },
    }] : []),
    // Cutoff — only if race with cutoff data
    ...(hasCutoffs ? [{
      field: 'cutoff',
      headerName: 'Cutoff',
      width: 115,
      renderCell: (params) => (
        <Typography variant="caption" color={params.value ? '#ef9a9a' : colors.grey[500]}>
          {params.value || '-'}
        </Typography>
      ),
    }] : []),
    // Time diff vs cutoff
    ...(hasCutoffs && hasTiming ? [{
      field: 'time_diff',
      headerName: 'Time Diff',
      width: 100,
      valueFormatter: (value) => {
        if (value == null) return '-';
        const abs = Math.abs(value);
        const hh = String(Math.floor(abs / 60)).padStart(2, '0');
        const mm = String(abs % 60).padStart(2, '0');
        return `${value >= 0 ? '+' : '-'}${hh}:${mm}`;
      },
      valueGetter: (_val, row) => {
        if (!row.cutoff || row.elapsed_s == null) return null;
        const cutoffMs = parseCutoffMs(row.cutoff, workoutStartTime);
        if (cutoffMs == null) return null;
        const startMs   = new Date(workoutStartTime).getTime();
        const arrivalMs = startMs + row.elapsed_s * 1000;
        return Math.round((arrivalMs - cutoffMs) / 60000); // positive = late
      },
      renderCell: (params) => {
        const diff = params.value;
        if (diff == null) return <Typography variant="caption" color={colors.grey[500]}>-</Typography>;
        const isOver = diff >= 0;
        const color  = isOver ? '#ef9a9a' : diff >= -30 ? '#ffcc80' : '#a5d6a7';
        const abs    = Math.abs(diff);
        const hh     = String(Math.floor(abs / 60)).padStart(2, '0');
        const mm     = String(abs % 60).padStart(2, '0');
        return (
          <Typography variant="caption" fontWeight="bold" color={color}>
            {isOver ? '+' : '-'}{hh}:{mm}
          </Typography>
        );
      },
    }] : []),
    // Moving pace
    {
      field: 'moving_pace',
      headerName: 'Mov. Pace',
      width: 90,
      valueFormatter: (value) => value ? formatPace(value, units) : '-',
      renderCell: (params) => (
        <Typography variant="body2" color={params.value ? colors.greenAccent[400] : colors.grey[500]}>
          {params.value ? formatPace(params.value, units) : '-'}
        </Typography>
      ),
    },
    // Moving normalised pace (GAP)
    {
      field: 'moving_gap',
      headerName: 'Mov. Norm. Pace',
      width: 120,
      valueFormatter: (value) => value ? formatPace(value, units) : '-',
      renderCell: (params) => (
        <Typography variant="body2" color={params.value ? colors.blueAccent[400] : colors.grey[500]}>
          {params.value ? formatPace(params.value, units) : '-'}
        </Typography>
      ),
    },
    // Ascending vertical speed — only shown on climb rows
    ...(hasAscVS ? [{
      field: 'moving_asc_vertical_speed',
      headerName: 'Asc V.Speed',
      width: 105,
      renderCell: (params) => {
        if (params.row.segment_type === 'descend') return null;
        return (
          <Typography variant="body2" color={params.value ? '#ef9a9a' : colors.grey[500]}>
            {params.value ? formatVerticalSpeed(params.value, units) : '-'}
          </Typography>
        );
      },
    }] : []),
    // Descending vertical speed — only shown on descend rows
    ...(hasDescVS ? [{
      field: 'moving_desc_vertical_speed',
      headerName: 'Desc V.Speed',
      width: 110,
      renderCell: (params) => {
        if (params.row.segment_type === 'climb') return null;
        return (
          <Typography variant="body2" color={params.value ? '#80cbc4' : colors.grey[500]}>
            {params.value ? formatVerticalSpeed(params.value, units) : '-'}
          </Typography>
        );
      },
    }] : []),
    // Checkpoint actions. Only rendered for the owner (the shared read-only view
    // passes no handlers), and only ever one affordance per row: insert after a
    // route segment, edit/remove on a checkpoint the user placed themselves.
    ...(onInsertCheckpoint ? [{
      field: '_cpActions',
      headerName: '',
      width: 84,
      sortable: false,
      filterable: false,
      align: 'center',
      renderCell: (params) => {
        const row = params.row;
        if (row.manual_end_id) {
          return (
            <Box display="flex" gap={0.25}>
              <Tooltip title={`Edit "${row.end_label}"`}>
                <span>
                  <IconButton size="small" disabled={checkpointBusy}
                    onClick={(e) => { e.stopPropagation(); onEditCheckpoint?.(row); }}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={`Remove "${row.end_label}"`}>
                <span>
                  <IconButton size="small" disabled={checkpointBusy}
                    onClick={(e) => { e.stopPropagation(); onDeleteCheckpoint?.(row.manual_end_id); }}>
                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        }
        if (row.segment_type === 'checkpoint') return null;   // organiser's — read-only
        return (
          <Tooltip title="Add a checkpoint here">
            <span>
              <IconButton size="small" disabled={checkpointBusy}
                className="cp-insert-btn"
                onClick={(e) => { e.stopPropagation(); onInsertCheckpoint?.(row); }}>
                <AddLocationAltIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    }] : []),
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="h6" color={colors.grey[100]}>Checkpoints Plan</Typography>
            {/* Sits with the title because it acts on this table, not on the tab. */}
            {onAddCheckpoint && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddLocationAltIcon sx={{ fontSize: 16 }} />}
                onClick={onAddCheckpoint}
                disabled={checkpointBusy}
                sx={{ fontSize: '0.72rem', textTransform: 'none', py: 0.1 }}
              >
                Add checkpoint
              </Button>
            )}
          </Box>
          <Typography variant="body2" color={colors.grey[300]}>
            {race?.name ? `${race.name} — ` : ''}{rows.length} segments
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip label="Climb"   size="small" sx={{ backgroundColor: '#ffa020', color: '#fff', fontSize: '0.7rem' }} />
          <Chip label="Descent" size="small" sx={{ backgroundColor: '#3b82f6', color: '#fff', fontSize: '0.7rem' }} />
          <Chip label="Mixed"   size="small" sx={{ backgroundColor: '#1a4a2e', color: '#86efac', fontSize: '0.7rem' }} />
          <Chip label="Rolling" size="small" sx={{ backgroundColor: '#546e7a', color: '#fff', fontSize: '0.7rem' }} />
          <Chip
            icon={<PauseCircleIcon sx={{ fontSize: 14, color: `${CHECKPOINT_COLOR} !important` }} />}
            label="Stop"
            size="small"
            sx={{ backgroundColor: '#1a3a52', color: CHECKPOINT_COLOR, fontSize: '0.7rem' }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          // Short plans size to their content: a fixed height has to guess the
          // grid's chrome (toolbar 52 + column headers 58 + footer 52 = 162px),
          // and guessing it low silently clips the rows — a 1-row Start→Finish
          // plan got 10px of viewport and showed nothing at all. Long plans keep
          // a capped, internally-scrolling grid so the page stays navigable.
          height: useAutoHeight ? 'auto' : GRID_MAX_HEIGHT,
          width: '100%',
          '& .MuiDataGrid-root': { border: 'none', backgroundColor: colors.grey[800] },
          '& .MuiDataGrid-toolbarContainer': {
            backgroundColor: colors.grey[900],
            borderBottom: `1px solid ${colors.grey[700]}`,
            '& .MuiButton-root': { color: colors.grey[300] },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${colors.grey[700]}`,
            color: colors.grey[100],
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.grey[900],
            borderBottom: `2px solid ${colors.grey[700]}`,
          },
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', color: colors.grey[100] },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${colors.grey[700]}`,
            backgroundColor: colors.grey[900],
          },
          '& .MuiTablePagination-root': { color: colors.grey[100] },
          '& .MuiDataGrid-row': { '&:hover': { backgroundColor: colors.grey[700] } },
          '& .row-checkpoint': {
            backgroundColor: '#0d2233',
            borderLeft: `3px solid ${CHECKPOINT_COLOR}`,
            '&:hover': { backgroundColor: '#12324a' },
          },
          '& .row-climb': {
            backgroundColor: '#1a1000',
            '&:hover': { backgroundColor: '#281900' },
          },
          '& .row-descend': {
            backgroundColor: '#00111a',
            '&:hover': { backgroundColor: '#001c2b' },
          },
          '& .row-mixed': {
            backgroundColor: '#0a1f10',
            '&:hover': { backgroundColor: '#0f2e18' },
          },
          '& .row-selected': {
            outline: '2px solid #ffeb3b',
            outlineOffset: '-2px',
            backgroundColor: '#1a1800 !important',
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={100}
          rowsPerPageOptions={[50, 100, 200]}
          disableSelectionOnClick
          rowHeight={52}
          autoHeight={useAutoHeight}
          showToolbar
          onRowClick={(params) => {
            const seg = params.row;
            if (seg.segment_type === 'checkpoint') return;
            const newId = seg.id === selectedId ? null : seg.id;
            setSelectedId(newId);
            if (onSegmentSelect) {
              onSegmentSelect(newId === null ? null : seg);
            }
          }}
          onRowDoubleClick={(params) => {
            const seg = params.row;
            if (seg.segment_type === 'checkpoint') return;
            if (onSegmentDblClick) onSegmentDblClick(seg);
          }}
          getRowClassName={(params) => {
            const t = params.row.segment_type;
            const selected = params.row.id === selectedId ? ' row-selected' : '';
            if (t === 'checkpoint') return `row-checkpoint${selected}`;
            if (t === 'climb')      return `row-climb${selected}`;
            if (t === 'descend')    return `row-descend${selected}`;
            if (t === 'mixed')      return `row-mixed${selected}`;
            return selected.trim();
          }}
          sx={{ cursor: 'pointer' }}
        />
      </Box>
    </Box>
  );
};

export default RacePlanGrid;
